import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import type { PurchaseOrder } from '../../types/purchaseOrder';
import { formatCurrency } from '../../utils/format';

const styles = StyleSheet.create({
  page: {
    paddingTop: 40,
    paddingHorizontal: 40,
    paddingBottom: 60,
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: '#111',
  },
  headerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  companyBlock: {
    maxWidth: '60%',
  },
  companyName: {
    fontSize: 14,
    fontWeight: 700,
    marginBottom: 4,
  },
  companyLine: {
    fontSize: 10,
    color: '#555',
  },
  logoImage: {
    maxHeight: 64,
    maxWidth: 160,
    objectFit: 'contain',
  },
  logoCircle: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: '#1f3c88',
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoText: {
    color: '#fff',
    fontSize: 9,
    fontWeight: 700,
    textAlign: 'center',
    paddingHorizontal: 6,
  },
  invoiceTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: '#1f3c88',
    marginBottom: 16,
  },
  summaryRow: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  summaryColumn: {
    flex: 1,
    paddingRight: 12,
  },
  summaryColumnRight: {
    flex: 1,
    paddingLeft: 12,
    alignItems: 'flex-start',
  },
  summaryLabel: {
    fontSize: 9,
    fontWeight: 700,
    color: '#666',
    marginBottom: 4,
  },
  summaryBox: {
    borderWidth: 1,
    borderColor: '#e5e7eb',
    paddingVertical: 6,
    paddingHorizontal: 6,
    minHeight: 46,
    justifyContent: 'flex-start',
  },
  summaryName: {
    fontSize: 10,
    fontWeight: 700,
    marginBottom: 2,
  },
  summaryText: {
    fontSize: 9,
    color: '#444',
  },
  metaRow: {
    flexDirection: 'row',
    marginBottom: 2,
  },
  metaLabel: {
    width: 90,
    fontSize: 9,
    color: '#555',
  },
  metaValue: {
    fontSize: 9,
    fontWeight: 700,
    color: '#111',
  },
  shipRow: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    marginTop: 8,
    marginBottom: 20,
  },
  shipLabel: {
    fontSize: 9,
    fontWeight: 700,
    color: '#666',
    marginRight: 6,
  },
  shipValue: {
    fontSize: 9,
  },
  table: {
    marginTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#d1d5db',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#e5e7f3',
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#d1d5db',
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  colDescription: {
    flex: 4,
    paddingLeft: 0,
  },
  colQty: {
    flex: 1.2,
    textAlign: 'center',
  },
  colReceived: {
    flex: 1.2,
    textAlign: 'center',
  },
  colRate: {
    flex: 2,
    textAlign: 'right',
  },
  colAmount: {
    flex: 2,
    textAlign: 'right',
    paddingRight: 0,
  },
  tableHeaderText: {
    fontSize: 9,
    fontWeight: 700,
    color: '#4b5563',
  },
  tableCellText: {
    fontSize: 9,
    color: '#111827',
  },
  totalsWrapper: {
    marginTop: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  paymentInfo: {
    flex: 2,
  },
  paymentText: {
    fontSize: 9,
    color: '#444',
    marginBottom: 2,
  },
  totalsBlock: {
    width: 220,
    alignSelf: 'flex-end',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  totalLabel: {
    fontSize: 9,
    color: '#6b7280',
  },
  totalValue: {
    fontSize: 9,
    fontWeight: 700,
    textAlign: 'right',
  },
  balanceLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: '#374151',
  },
  balanceValue: {
    fontSize: 14,
    fontWeight: 700,
    color: '#111827',
    textAlign: 'right',
  },
  balanceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
    paddingTop: 6,
    borderTopWidth: 1,
    borderTopColor: '#d1d5db',
  },
  statusBadge: {
    fontSize: 9,
    fontWeight: 700,
    color: '#1f3c88',
    textTransform: 'uppercase',
  },
});

interface PurchaseOrderPDFProps {
  order: PurchaseOrder;
  companyInfo?: {
    name: string;
    address: string;
    email?: string;
    phone?: string;
    logoDataUrl?: string | null;
  } | null;
  supplierInfo?: {
    name: string;
    email?: string;
    phone?: string;
    contactName?: string;
  } | null;
  currencySymbol: string;
  showReceivedColumn?: boolean;
}

const defaultCompanyInfo = {
  name: 'Coffee Roasters Inc.',
  address: '123 Coffee Bean Lane, Brew City, BC 90210',
  email: 'billing@coffeeroasters.com',
  phone: '+1 (555) 123-4567',
  logoDataUrl: undefined,
};

const formatDisplayDate = (value?: string | null): string => {
  if (!value) {
    return '';
  }
  const parts = value.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return value;
};

const statusLabels: Record<string, string> = {
  draft: 'Draft',
  ordered: 'Ordered',
  cancelled: 'Cancelled',
  pending: 'Pending',
  shipping: 'Shipping',
  received: 'Received',
  unpaid: 'Unpaid',
  paid: 'Paid',
  refunded: 'Refunded',
};

const formatStatus = (value?: string | null): string => {
  if (!value) return '-';
  return statusLabels[value] || value;
};

export const PurchaseOrderInvoicePDF: React.FC<PurchaseOrderPDFProps> = ({
  order,
  companyInfo,
  supplierInfo,
  currencySymbol,
  showReceivedColumn,
}) => {
  const company = companyInfo || defaultCompanyInfo;
  const orderDate = order.orderDate || '';
  const expectedDelivery = order.expectedDeliveryDate || '';
  const actualDelivery = order.actualDeliveryDate || '';

  const includeReceived =
    showReceivedColumn ?? order.items.some((item) => item.weightReceived != null && item.weightReceived > 0);

  const itemSubtotal = order.items.reduce((sum, item) => {
    const unitPrice = item.unitPrice || 0;
    const qty = item.quantity || 0;
    return sum + unitPrice * qty;
  }, 0);

  const expensesTotal = (order.purchaseExpenses || []).reduce(
    (sum, expense) => sum + (expense.amount || 0),
    0,
  );
  const grandTotal = itemSubtotal + expensesTotal;

  const supplierName = supplierInfo?.name || order.supplierName || order.supplier;
  const supplierLines: string[] = [];
  if (supplierName) supplierLines.push(supplierName);
  if (supplierInfo?.contactName) supplierLines.push(`Contact: ${supplierInfo.contactName}`);
  if (supplierInfo?.email) supplierLines.push(supplierInfo.email);
  if (supplierInfo?.phone) supplierLines.push(supplierInfo.phone);

  const deliverToLines: string[] = [];
  if (company.name) deliverToLines.push(company.name);
  if (company.address) deliverToLines.push(company.address);

  const logoInitials =
    company.name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase() || 'CO';

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.headerTop}>
          <View style={styles.companyBlock}>
            <Text style={styles.companyName}>{company.name}</Text>
            {company.address ? <Text style={styles.companyLine}>{company.address}</Text> : null}
            {company.phone ? <Text style={styles.companyLine}>{company.phone}</Text> : null}
            {company.email ? <Text style={styles.companyLine}>{company.email}</Text> : null}
          </View>
          {company.logoDataUrl ? (
            <Image src={company.logoDataUrl} style={styles.logoImage} />
          ) : (
            <View style={styles.logoCircle}>
              <Text style={styles.logoText}>{logoInitials}</Text>
            </View>
          )}
        </View>

        <Text style={styles.invoiceTitle}>PURCHASE ORDER</Text>

        <View style={styles.summaryRow}>
          <View style={styles.summaryColumn}>
            <Text style={styles.summaryLabel}>SUPPLIER</Text>
            <View style={styles.summaryBox}>
              {supplierLines.length > 0 ? (
                <>
                  <Text style={styles.summaryName}>{supplierLines[0]}</Text>
                  {supplierLines.slice(1).map((line) => (
                    <Text key={line} style={styles.summaryText}>
                      {line}
                    </Text>
                  ))}
                </>
              ) : (
                <Text style={styles.summaryText}>No supplier info</Text>
              )}
            </View>
          </View>

          <View style={styles.summaryColumn}>
            <Text style={styles.summaryLabel}>DELIVER TO</Text>
            <View style={styles.summaryBox}>
              {deliverToLines.length > 0 ? (
                <>
                  <Text style={styles.summaryName}>{deliverToLines[0]}</Text>
                  {deliverToLines.slice(1).map((line) => (
                    <Text key={line} style={styles.summaryText}>
                      {line}
                    </Text>
                  ))}
                </>
              ) : (
                <Text style={styles.summaryText}>No delivery address</Text>
              )}
            </View>
          </View>

          <View style={styles.summaryColumnRight}>
            <Text style={styles.summaryLabel}>PO DETAILS</Text>
            <View>
              <View style={styles.metaRow}>
                <Text style={styles.metaLabel}>PO NO.</Text>
                <Text style={styles.metaValue}>{order.orderNumber || 'DRAFT'}</Text>
              </View>
              <View style={styles.metaRow}>
                <Text style={styles.metaLabel}>ORDER DATE</Text>
                <Text style={styles.metaValue}>{formatDisplayDate(orderDate)}</Text>
              </View>
              <View style={styles.metaRow}>
                <Text style={styles.metaLabel}>EXPECTED</Text>
                <Text style={styles.metaValue}>{formatDisplayDate(expectedDelivery) || '-'}</Text>
              </View>
              <View style={styles.metaRow}>
                <Text style={styles.metaLabel}>PAYMENT</Text>
                <Text style={styles.metaValue}>{order.paymentType || '-'}</Text>
              </View>
              <View style={styles.metaRow}>
                <Text style={styles.metaLabel}>STATUS</Text>
                <Text style={styles.statusBadge}>{formatStatus(order.status)}</Text>
              </View>
            </View>
          </View>
        </View>

        {actualDelivery ? (
          <View style={styles.shipRow}>
            <Text style={styles.shipLabel}>ACTUAL DELIVERY</Text>
            <Text style={styles.shipValue}>{formatDisplayDate(actualDelivery)}</Text>
          </View>
        ) : null}

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.colDescription, styles.tableHeaderText]}>DESCRIPTION</Text>
            <Text style={[styles.colQty, styles.tableHeaderText]}>WEIGHT (KG)</Text>
            {includeReceived ? (
              <Text style={[styles.colReceived, styles.tableHeaderText]}>RECEIVED (KG)</Text>
            ) : null}
            <Text style={[styles.colRate, styles.tableHeaderText]}>UNIT PRICE</Text>
            <Text style={[styles.colAmount, styles.tableHeaderText]}>AMOUNT</Text>
          </View>
          {order.items.map((item, index) => {
            const qty = item.quantity || 0;
            const unitPrice = item.unitPrice || 0;
            const lineAmount = qty * unitPrice;
            const itemKey = item.inventoryItemId || item.itemCode || `${item.name}-${index}`;
            return (
              <View key={`${itemKey}-${index}`} style={styles.tableRow}>
                <Text style={[styles.colDescription, styles.tableCellText]}>
                  {item.name}
                  {item.sampleItem ? ' (Sample)' : ''}
                </Text>
                <Text style={[styles.colQty, styles.tableCellText]}>{qty.toFixed(3)}</Text>
                {includeReceived ? (
                  <Text style={[styles.colReceived, styles.tableCellText]}>
                    {item.weightReceived != null ? Number(item.weightReceived).toFixed(3) : '-'}
                  </Text>
                ) : null}
                <Text style={[styles.colRate, styles.tableCellText]}>
                  {formatCurrency(unitPrice, currencySymbol)}
                </Text>
                <Text style={[styles.colAmount, styles.tableCellText]}>
                  {formatCurrency(lineAmount, currencySymbol)}
                </Text>
              </View>
            );
          })}

          {(order.purchaseExpenses || []).map((expense, index) => (
            <View key={`expense-${index}`} style={styles.tableRow}>
              <Text style={[styles.colDescription, styles.tableCellText]}>
                {expense.description || expense.itemName || expense.expenseType}
              </Text>
              <Text style={[styles.colQty, styles.tableCellText]}>
                {expense.quantityLoss ? expense.quantityLoss.toFixed(3) : '-'}
              </Text>
              {includeReceived ? <Text style={[styles.colReceived, styles.tableCellText]}>-</Text> : null}
              <Text style={[styles.colRate, styles.tableCellText]}>
                {expense.unitPrice ? formatCurrency(expense.unitPrice, currencySymbol) : '-'}
              </Text>
              <Text style={[styles.colAmount, styles.tableCellText]}>
                {formatCurrency(expense.amount || 0, currencySymbol)}
              </Text>
            </View>
          ))}
        </View>

        <View style={styles.totalsWrapper}>
          <View style={styles.paymentInfo}>
            {order.paymentNotes ? (
              <Text style={styles.paymentText}>{order.paymentNotes}</Text>
            ) : null}
            {order.notes ? <Text style={styles.paymentText}>{order.notes}</Text> : null}
            <Text style={styles.paymentText}>{'\n\n'}{company.name}</Text>
          </View>
          <View style={styles.totalsBlock}>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Subtotal</Text>
              <Text style={styles.totalValue}>{formatCurrency(itemSubtotal, currencySymbol)}</Text>
            </View>
            {expensesTotal > 0 ? (
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Expenses / Adjustments</Text>
                <Text style={styles.totalValue}>{formatCurrency(expensesTotal, currencySymbol)}</Text>
              </View>
            ) : null}
            <View style={styles.balanceRow}>
              <Text style={styles.balanceLabel}>TOTAL</Text>
              <Text style={styles.balanceValue}>
                {formatCurrency(grandTotal, currencySymbol)}
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};
